import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToId, goToTab } from "@/lib/lenis";

const LINKS = [
  { label: "The Problem", id: "#problem", testid: "nav-link-problem" },
  { label: "Services", id: "#services", testid: "nav-link-services" },
  { label: "The Advantage", id: "#advantage", testid: "nav-link-advantage" },
  { label: "About Thom", id: "#about", testid: "nav-link-about" },
  { label: "FAQ", id: "#faq", testid: "nav-link-faq" },
  { label: "Contact", id: "#contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  const assessment = () => {
    setOpen(false);
    goToTab("assessment");
    scrollToId("#contact");
  };

  return (
    <header
      data-testid="main-navigation-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-[#E2E8F0] bg-white/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 text-[#0F141A] lg:px-12">
        <a href="#top" data-testid="brand-logo-link" onClick={go("#top")} className="shrink-0">
          <Logo />
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={l.testid}
              onClick={go(l.id)}
              className="group relative text-[13px] font-medium text-[#4A5361] transition-colors duration-200 hover:text-[#EB5E28]"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#EB5E28] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#0F141A] shadow-sm lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-t border-[#E2E8F0] bg-white/95 px-6 pb-8 pt-4 backdrop-blur-xl lg:hidden"
          >
            {LINKS.map((l, i) => (
              <motion.a
                key={l.id}
                href={l.id}
                data-testid={l.testid}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
                onClick={go(l.id)}
                className="block w-full border-b border-[#F1F5F9] py-4 text-left font-display text-2xl text-[#0F141A]"
              >
                {l.label}
              </motion.a>
            ))}
            <button
              data-testid="mobile-free-assessment-button"
              onClick={assessment}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#EB5E28] px-5 py-3.5 text-sm font-medium text-white shadow-md shadow-[#EB5E28]/20"
            >
              Schedule a HITL Assessment
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
